import type { AvailableToSpendDay } from '@/types';
import { formatDayMonth } from './dailyMeta';

/** Everything the timeline hover-tooltip says about a day. */
export function timelineDayDescription(
  day: AvailableToSpendDay,
  formatCurrency: (value: number, opts?: { noCents?: boolean }) => string,
): string {
  const dayLabel = formatDayMonth(day.date) ?? day.date;
  const parts = [dayLabel];
  if (day.kind === 'today') parts.push('hoje');
  if (day.kind !== 'future') {
    if (day.entered > 0) parts.push(`entrou ${formatCurrency(day.entered, { noCents: true })}`);
    parts.push(day.spent > 0 ? `gastou ${formatCurrency(day.spent, { noCents: true })}` : 'sem gastos');
  }
  if (day.committed > 0) {
    parts.push(`${formatCurrency(day.committed, { noCents: true })} em compromissos`);
    if (day.labels.length > 0) parts.push(day.labels.join(', '));
  } else if (day.kind !== 'future' && day.labels.length > 0) {
    parts.push(day.labels.join(', '));
  }
  if (day.salary) parts.push('salário');
  return parts.join(' · ');
}

export type TimelineMarker =
  | { type: 'salario' }
  | { type: 'entrada' }
  | { type: 'despesa'; count: number; isFatura: boolean };

/**
 * The day's "important day" markers, at most ONE PER CATEGORY, in the fixed
 * order salário → entrada → despesa. Salary (received on elapsed days,
 * expected on the window end) beats generic income — the payload can't split
 * a salary day's total, so the two never coexist. The expense marker groups
 * everything due (future/today) or paid (past) on the day; `isFatura` picks
 * the card icon when any commitment label is one (the backend names them
 * "fatura X"). Today itself is marked by the cell ring, so its own events
 * still get their markers.
 */
export function timelineDayMarkers(day: AvailableToSpendDay): TimelineMarker[] {
  const markers: TimelineMarker[] = [];
  if (day.salary) {
    markers.push({ type: 'salario' });
  } else if (day.kind !== 'future' && (day.entered ?? 0) > 0) {
    markers.push({ type: 'entrada' });
  }

  const hasExpense = day.kind === 'past' ? day.labels.length > 0 : day.committed > 0;
  if (hasExpense) {
    markers.push({
      type: 'despesa',
      count: Math.max(1, day.labels.length),
      isFatura: day.labels.some((label) => label.toLowerCase().startsWith('fatura')),
    });
  }
  return markers;
}

/** Whether the day opens the details popover: elapsed days always have a story
 * (money in/out); future days only when something is marked on them. */
export function isTimelineDayInteractive(day: AvailableToSpendDay): boolean {
  return day.kind !== 'future' || timelineDayMarkers(day).length > 0;
}
